<script setup lang="ts">
import { computed, type StyleValue } from 'vue';
import {
    toAddressingCountryCode,
    type AddressingCountryCode,
    type AddressingLocaleKey,
} from '@precon-tools-suite-by-shared/js-commerceguys-addressing';
import { useAddressingCountry } from './countries';
import CountryFlag from './CountryFlag.vue';

const props = withDefaults(
    defineProps<{
        code: string | null | undefined;
        fallbackCode?: AddressingCountryCode;
        hideFlag?: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        flagClass?: any;
        flagStyle?: StyleValue;
        flagSquare?: boolean;
        flagRounded?: boolean;
        flagSize?: 'md' | 'lg';
        hideLabel?: boolean;
        label?: 'code' | 'name' | 'threeLetterCode' | 'currencyCode';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        labelClass?: any;
        labelStyle?: StyleValue;
        locale?: string;
        fallbackLocale?: AddressingLocaleKey;
    }>(),
    {
        fallbackCode: undefined,
        flagClass: undefined,
        flagSize: undefined,
        flagStyle: undefined,
        label: 'name',
        labelClass: undefined,
        labelStyle: undefined,
        locale: undefined,
        fallbackLocale: 'en',
    }
);

const countryCode = computed(
    () => toAddressingCountryCode(props.code) ?? props.fallbackCode
);

const country = useAddressingCountry(() => props.code, {
    fallbackCountryCode: () => props.fallbackCode,
    fallbackLocale: props.fallbackLocale,
    locale: () => props.locale,
});

const showFlag = computed(() => !props.hideFlag && !!countryCode.value);
const _label = computed(
    () => country.value?.[props.label] ?? countryCode.value
);
</script>

<template>
    <span class="country-span" style="white-space: nowrap">
        <template v-if="showFlag">
            <slot
                name="flag"
                :country="country"
                :country-code="countryCode"
                :class="flagClass"
                :square="flagSquare"
                :rounded="flagRounded"
                :style="flagStyle"
                :size="flagSize"
            >
                <CountryFlag
                    class="country-span-flag"
                    :country-code="countryCode"
                    :class="flagClass"
                    :square="flagSquare"
                    :rounded="flagRounded"
                    :style="flagStyle"
                    :size="flagSize"
                />
            </slot>
        </template>
        <template v-if="!hideLabel">
            <slot
                :country="country"
                :country-code="countryCode"
                :class="labelClass"
                :style="labelStyle"
            >
                <span
                    class="country-span-label"
                    :class="labelClass"
                    :style="labelStyle"
                    >{{ _label }}</span
                >
            </slot>
        </template>
    </span>
</template>
